import React, { useEffect } from 'react';

const OPERATORS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'isNot', label: 'Is not' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' }
];

const AdvancedFilterSection = ({ fields, filters = [], onChange }) => {
  // Initialize with empty filter if none exist
  useEffect(() => {
    if (filters.length === 0) {
      onChange([{ operator: 'contains' }]);
    }
  }, [filters.length, onChange]); // Run when filters length changes

  const handleRemoveFilter = (index) => {
    const newFilters = [...filters];
    newFilters.splice(index, 1);
    // If we removed the last filter, add a new empty one
    if (newFilters.length === 0) {
      newFilters.push({ operator: 'contains' });
    }
    onChange(newFilters);
  };

  const handleFilterChange = (index, field, value) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [field]: value };
    onChange(newFilters);
  };

  const handleAddFilter = (index, logicOperator) => {
    const newFilters = [...filters];
    // Add logicOperator to the current filter
    newFilters[index] = { ...newFilters[index], logicOperator };
    // Add a new filter with default operator
    newFilters.push({ operator: 'contains' });
    onChange(newFilters);
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {filters.map((filter, index) => (
        <div key={index} className="grid grid-cols-[auto,3fr,2fr,3fr,auto] gap-6 items-center">
          <button
            type="button"
            id={`remove-filter-${index}`}
            name={`remove-filter-${index}`}
            onClick={() => handleRemoveFilter(index)}
            className="p-2 text-gray-400 hover:text-gray-500"
            aria-label="Remove filter"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div>
            <label htmlFor={`filter-field-${index}`} className="sr-only">Select field</label>
            <select
              id={`filter-field-${index}`}
              name={`filter-field-${index}`}
              value={filter.column || ''}
              onChange={(e) => handleFilterChange(index, 'column', e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select a field</option>
              {fields.map(field => (
                <option key={field.id} value={field.id}>
                  {field.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`filter-operator-${index}`} className="sr-only">Select operator</label>
            <select
              id={`filter-operator-${index}`}
              name={`filter-operator-${index}`}
              value={filter.operator || 'contains'}
              onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {OPERATORS.map(op => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>
          {!['isEmpty', 'isNotEmpty'].includes(filter.operator) && (
            <div>
              <label htmlFor={`filter-value-${index}`} className="sr-only">Filter value</label>
              <input
                type="text"
                id={`filter-value-${index}`}
                name={`filter-value-${index}`}
                value={filter.value || ''}
                onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter value..."
              />
            </div>
          )}
          <div className="flex gap-2 items-center">
            <button
              type="button"
              id={`add-and-filter-${index}`}
              name={`add-and-filter-${index}`}
              onClick={() => handleAddFilter(index, 'AND')}
              className={`px-3 py-2 text-sm border rounded-md ${
                filter.logicOperator === 'AND'
                  ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-300'
              }`}
            >
              AND
            </button>
            <button
              type="button"
              id={`add-or-filter-${index}`}
              name={`add-or-filter-${index}`}
              onClick={() => handleAddFilter(index, 'OR')}
              className={`px-3 py-2 text-sm border rounded-md ${
                filter.logicOperator === 'OR'
                  ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-300'
              }`}
            >
              OR
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdvancedFilterSection;
